// The Online panel of the play view (standard 1.2, plan.md §19): create a room, join one by code or invite link,
// see who is in it, start (room host) or leave. Nothing here comes from another player as text: peers are shown
// by player number, round trip and state only.
import { useEffect, useState } from 'preact/hooks';
import { cleanRoomCode, PEER_CONNECTED, PEER_GONE, PEER_SILENT } from '../../shared/net.ts';
import type { LobbyPeer, NetSession } from '../net/session.ts';

/** Re-render whenever the session changes. */
export function useNet(net: NetSession | undefined): void {
  const [, set] = useState(0);
  useEffect(() => net?.subscribe(() => set((n) => n + 1)), [net]);
}

export function inviteLink(code: string, loc: { origin: string; pathname: string; search: string } = location): string {
  return `${loc.origin}${loc.pathname}${loc.search}#room=${code}`;
}

/** A room code from an invite link's fragment (#room=CODE), or null. */
export function inviteCode(hash: string): string | null {
  const m = /^#room=([A-Za-z0-9]{6})$/.exec(hash);
  return m ? cleanRoomCode(m[1]) : null;
}

function peerText(net: NetSession, p: LobbyPeer): string {
  if (p.match === 'build') return 'different build: refused';
  if (p.match === 'rom') return 'different ROM: refused';
  if (p.state === PEER_GONE) return 'left';
  if (p.state === -1 || p.match === 'pending') return 'connecting…';
  const ms = p.rtt >= 0 ? `${p.rtt} ms` : '… ms';
  const via = p.direct || p.id === net.hostId || net.isHost() ? '' : ', through the room host';
  return `${p.state === PEER_SILENT ? 'not responding' : 'ready'}, ${ms}${via}${p.hidden ? ', tab hidden' : ''}`;
}

export function OnlinePanel({ net, max, onClose }: { net: NetSession; max: number; onClose: () => void }) {
  useNet(net);
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const join = () => {
    const c = cleanRoomCode(code);
    if (c) net.join(c);
  };
  const link = net.code ? inviteLink(net.code) : '';
  const copy = () => { navigator.clipboard?.writeText(link).then(() => setCopied(true), () => {}); };
  const players = net.peers().filter((p) => p.state !== PEER_GONE || p.match !== 'ok');
  return (
    <div class="menu online" role="dialog" aria-label="Online" data-state="online" data-phase={net.phase}>
      <div class="menu-head">
        <strong>Online</strong>
        <button data-action="close-online" onClick={onClose}>Back to game</button>
      </div>
      <p class="muted small" data-field="net-privacy">
        Online play connects your browser directly to the other players' browsers (WebRTC), so <strong>they can see
        your IP address</strong>, and you theirs. Play only with people you trust. The room server only passes the
        connection set-up between you; no game data goes through it.
      </p>
      {net.error && <p class="warning" role="alert" data-field="net-error">{net.error}</p>}

      {net.phase === 'idle' && (
        <section class="menu-section">
          <p>Up to {max} players. Everyone needs this same game build and the same ROM.</p>
          <p><button class="primary" data-action="net-create" onClick={() => net.create()}>Create a room</button></p>
          <form onSubmit={(e) => { e.preventDefault(); join(); }}>
            <label>Room code{' '}
              <input data-field="net-code" value={code} maxLength={9} autocomplete="off" autocapitalize="characters"
                spellcheck={false} onInput={(e) => setCode((e.target as HTMLInputElement).value)} />
            </label>{' '}
            <button type="submit" data-action="net-join" disabled={!cleanRoomCode(code)}>Join</button>
          </form>
        </section>
      )}

      {net.phase === 'connecting' && <p data-field="net-status">Connecting to the room…</p>}

      {net.phase === 'lobby' && (
        <section class="menu-section">
          <p>Room code <strong class="room-code" data-field="room-code">{net.code}</strong></p>
          <p class="small">Invite link: <code data-field="invite-link">{link}</code>{' '}
            <button onClick={copy}>{copied ? 'Copied' : 'Copy'}</button></p>
          <p class="muted small">The room closes when the game starts, or after 10 minutes.</p>
          <ul class="net-players" data-field="net-players">
            <li data-peer={net.me} data-match="ok">Player {net.playerNumber(net.me)} (you){net.isHost() ? ', room host' : ''}</li>
            {players.map((p) => (
              <li key={p.id} data-peer={p.id} data-match={p.match} data-net-state={p.state}>
                Player {net.playerNumber(p.id)}{p.id === net.hostId ? ' (room host)' : ''}: {peerText(net, p)}
              </li>
            ))}
          </ul>
          {net.isHost()
            ? <p><button class="primary" data-action="net-start" disabled={!net.canStart()} onClick={() => net.start()}>
                Start ({net.ready().length + 1} players)</button>{' '}
                <span class="muted small">Everyone restarts the game from power-on with your options and your save.</span></p>
            : <p class="muted" data-field="net-status">Waiting for the room host to start.</p>}
          <p><button data-action="net-leave" onClick={() => net.leave()}>Leave the room</button></p>
        </section>
      )}

      {net.phase === 'session' && net.boot && (
        <section class="menu-section" data-field="net-session">
          <p>In an online session with {net.boot.ids.length} players: you are Player {net.boot.start.me + 1}.</p>
          <ul class="net-players" data-field="net-players">
            {net.peers().filter((p) => net.boot!.ids.includes(p.id)).map((p) => (
              <li key={p.id} data-peer={p.id} data-net-state={p.state}>
                Player {net.playerNumber(p.id)}: {p.state === PEER_CONNECTED ? `${p.rtt >= 0 ? p.rtt : '…'} ms${p.hidden ? ', tab hidden' : ''}`
                  : p.state === PEER_SILENT ? 'not responding' : 'left'}
              </li>
            ))}
          </ul>
          <p class="muted small">The game never pauses online. Keep this tab in front: a hidden tab can stall the game for everyone.</p>
          <p><button data-action="net-leave" onClick={() => net.leave()}>Leave the session</button></p>
        </section>
      )}
    </div>
  );
}
